import axios from 'helpers/api';

/**
 * A user's own notification preferences, web and mobile.
 *
 *   GET  /users/notification-preferences     everything the screen shows
 *   POST /users/optins-notifications         web:    { notifications: [{ facility_id, notification_id, is_active }] }
 *   POST /users/notification-subscriptions   mobile: one item per request (UI_endpoint_notifications.md §4)
 *
 * The user always comes from the login token, so user_id is never sent.
 */

const authHeaders = () => {
  const tokenStr = localStorage.getItem('accessToken');
  return tokenStr ? { Authorization: `Bearer ${tokenStr}` } : {};
};

const apiUrl = path => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || '';
  return baseUrl ? `${baseUrl}${path}` : path;
};

const hasValue = v => v !== undefined && v !== null && v !== '';

const toBool = (v, fallback) =>
  hasValue(v) ? v === true || v === 1 || v === '1' || v === 'true' : fallback;

const asArray = v => (Array.isArray(v) ? v : []);

export const errorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

export const webKey = (facilityId, notificationId) =>
  `${facilityId}:${notificationId}`;

// Render mobile types generically (types → items → a toggle per item) so new
// mobile-only types show up with no web change. A medpass item is one of the
// admin's reminders; any other kind has a single item standing for the type.
const normaliseMobileType = type => ({
  id: type.notification_id,
  name: type.name,
  kind: type.kind,
  configured: toBool(type.configured, true),
  items: asArray(type.items).map(item => {
    const notificationId = item.notification_id ?? type.notification_id;
    const reminderId = hasValue(item.medpass_reminder_id)
      ? item.medpass_reminder_id
      : null;
    return {
      key: reminderId !== null ? `r${reminderId}` : `n${notificationId}`,
      notificationId,
      reminderId,
      label: item.label || type.name,
      direction: item.direction || null,
      subscribed: toBool(item.subscribed, false),
      push: toBool(item.is_push_notification, true),
      email: toBool(item.is_email, false)
    };
  })
});

export const fetchPreferences = async () => {
  const { data } = await axios.get(apiUrl('/users/notification-preferences'), {
    headers: authHeaders()
  });
  if (data?.status === false) {
    throw new Error(
      data.message || 'Could not load your notification preferences.'
    );
  }

  return {
    webTypes: asArray(data?.web?.types).map(t => ({
      id: t.notification_id,
      name: t.name
    })),
    webSubscriptions: new Set(
      asArray(data?.web?.subscriptions)
        .filter(s => toBool(s.is_active, true))
        .map(s => webKey(s.facility_id, s.notification_id))
    ),
    mobileFacilities: asArray(data?.mobile?.facilities).map(f => ({
      id: String(f.facility_id),
      name: f.facility_name,
      types: asArray(f.types).map(normaliseMobileType)
    }))
  };
};

export const saveWebSubscriptions = async (
  facilityId,
  notificationIds,
  active
) => {
  const { data } = await axios.post(
    apiUrl('/users/optins-notifications'),
    {
      notifications: notificationIds.map(id => ({
        facility_id: Number(facilityId),
        notification_id: Number(id),
        is_active: active ? 1 : 0
      }))
    },
    { headers: authHeaders() }
  );
  if (!data?.status) {
    throw new Error(
      data?.message || 'Failed to update your web notifications.'
    );
  }
  return data;
};

export const saveMobileSubscription = async (
  facilityId,
  item,
  { subscribed, push, email }
) => {
  const body = { facility_id: Number(facilityId), active: subscribed ? 1 : 0 };
  if (item.reminderId !== null) body.medpass_reminder_id = item.reminderId;
  else body.notification_id = item.notificationId;
  // Unsubscribing leaves out the channels so the server keeps them for next time.
  if (subscribed) {
    body.is_push_notification = push ? 1 : 0;
    body.is_email = email ? 1 : 0;
  }

  const { data } = await axios.post(
    apiUrl('/users/notification-subscriptions'),
    body,
    {
      headers: authHeaders()
    }
  );
  if (data?.status === false) {
    throw new Error(
      data.message || 'Failed to update this mobile notification.'
    );
  }
  return data;
};
